import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardBody, Input, Button } from "reactstrap";
import Select from "react-select";
import { listAllSystems } from "services/listServices";
import styled from "@emotion/styled"
import { useHistory } from "react-router-dom"
import { BiArrowBack } from "react-icons/bi";
import { useSelector } from "react-redux";

const StyledButton = styled(Button)`
  width: 200px;
  margin: 30px auto;
  // display: block;
  display: flex;
  align-items: center; /* Align icon and text vertically */
  justify-content: center; /* Center text and icon */
  gap: 8px; 
`;

const StepOne = ({ form, formType, handleInputChange, changeTab, save, activeTab }) => {
  const [benchmarkingOptions, setBenchmarkingOptions] = useState([]);
  const [systemsCache, setSystemsCache] = useState(null);
  const [errors, setErrors] = useState({});
  const history = useHistory()// Cache fetched systems
  const { user } = useSelector(state => state.Profile)

  const trackerStagesOptions = useMemo(() => [
    { value: 1, label: "Initiation" },
    { value: 2, label: "Development" },
    { value: 3, label: "Validation" },
    { value: 4, label: "Approval" },
    { value: 5, label: "Legislation" },
    { value: 6, label: "Implementation" },
  ], []);

  const landscapingCategories = useMemo(() => [
    { value: "Macro", label: "Macro" },
    { value: "Meso", label: "Meso" },
    { value: "Micro", label: "Micro" },
  ], []);

  // Fetch systems data for benchmarking
  const loadSystems = useCallback(async () => {
    try {
      const data = await listAllSystems();
      return data.map(system => ({
        label: system.name,
        value: system.id,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  }, []);

  // Caching systems data to avoid redundant fetching
  useEffect(() => {
    const fetchSystems = async () => {
      if (formType === "benchmarking" && !systemsCache) {
        const systems = await loadSystems();

        setBenchmarkingOptions(systems);
        setSystemsCache(systems); // Cache the fetched systems
      } else if (systemsCache) {
        setBenchmarkingOptions(systemsCache); // Use cached data
      }
    };
    fetchSystems();
  }, [formType, systemsCache, loadSystems]);

  const validateForm = () => {
    const newErrors = {};

    if (!form.name || !form.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (formType === "tracker" && !form.stage_id) {
      newErrors.stage_id = "Stage is required";
    }

    if (formType === "benchmarking" && !form.system_id) {
      newErrors.system_id = "System is required";
    }

    if (formType === "landscaping") {
      if (!form.landscaping_category) {
        newErrors.landscaping_category = "Landscaping category is required";
      }
      // if (!form.description || !form.description.trim()) {
      //   newErrors.description = "Description is required";
      // }
    }
    return newErrors;
  };

  // Proceed to next step
  const proceed = () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return; // Stop if there are validation errors
    }
    setErrors({});
    // save(1);
    // alert("Milestone details saved in draft", true);
    changeTab(user?.user_country?.length === 1 ? "stepAddOwners" : "step2");
  };

  const handleFieldChange = (field, value) => {
    handleInputChange(field, value);
    if (errors[field]) {
      setErrors(prevErrors => {
        const newErr = { ...prevErrors };
        delete newErr[field];
        return newErr;
      });
    }
  };

  // Helper component for rendering select dropdowns
  const CustomSelect = ({ options, value, onChange, placeholder, error, field }) => (
    <div style={{ gridColumn: "1/-1" }}>
      <Select
        className="basic-single"
        classNamePrefix="select"
        options={options}
        value={value}
        // onChange={onChange}
        onChange={selectedOption => {
          onChange(selectedOption);
          if (error) {
            setErrors(prevErrors => {
              const newErr = { ...prevErrors };
              delete newErr[field];
              return newErr;
            });
          }
        }}
        placeholder={placeholder}
      />
      {error && <small className="text-danger">{error}</small>}
    </div>
  );

  const handleGoBack = () => {
    history.goBack();
  };

  return (
    <>
      <Card className="mt-5" style={{ border: "none" }}>
        <CardBody style={{ border: "2px solid #f4dac6", borderRadius: "5px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1em" }}>
            <label htmlFor="name" style={{ gridColumn: "1/-1" }}>
              Name <span style={{ color: "red" }}>*</span>
            </label>
            {/* Name field */}
            <Input
              type="text"
              style={{ borderRadius: 5, borderWidth: 1, gridColumn: "1/-1" }}
              className={`form-control  ${form.error && "border-danger"}`}
              id="name"
              value={form?.name}
              onChange={e => handleFieldChange("name", e.target.value)}
              placeholder="Name"
            />
            {errors.name && (
              <small className="text-danger" style={{ gridColumn: "1/-1" }}>
                {errors.name}
              </small>
            )}

            {/* E-Tracker fields */}
            {formType === "tracker" && (
              <>
                <label htmlFor="stage_id" style={{ gridColumn: "1/-1" }}>
                  Assign Stage <span style={{ color: "red" }}>*</span>
                </label>
                <CustomSelect
                  options={trackerStagesOptions}
                  value={trackerStagesOptions.find(option => option.value === form?.stage_id) || null}
                  onChange={option => handleFieldChange("stage_id", option.value)}
                  placeholder="Assign Stage"
                  error={errors.stage_id}
                  field="stage_id"
                />
              </>
            )}

            {/* Benchmarking fields */}
            {formType === "benchmarking" && (
              <>
                <label htmlFor="system_id" style={{ gridColumn: "1/-1" }}>
                  Select System <span style={{ color: "red" }}>*</span>
                </label>
                {/* Show a loading message while systems data is being fetched */}
                {benchmarkingOptions.length === 0 ? (
                  <p>Loading systems...</p>
                ) : (
                  <CustomSelect
                    options={benchmarkingOptions}
                    value={benchmarkingOptions.find(option => option.value === form?.system_id) || null}
                    onChange={option => handleFieldChange("system_id", option.value)}
                    placeholder="Select System"
                    error={errors.system_id}
                    field="system_id"
                  />
                )}
              </>
            )}

            {/* Landscaping fields */}
            {formType === "landscaping" && (
              <>
                <label htmlFor="landscaping_category" style={{ gridColumn: "1/-1" }}>
                  Landscaping Category <span style={{ color: "red" }}>*</span>
                </label>
                <CustomSelect
                  options={landscapingCategories}
                  value={landscapingCategories.find(option => option.value === form?.landscaping_category) || null}
                  onChange={option => handleFieldChange("landscaping_category", option.value)}
                  placeholder="Select Landscaping Category"
                  error={errors.landscaping_category}
                  field="landscaping_category"
                />

                {/* Description field */}
                <label htmlFor="description" style={{ gridColumn: "1/-1" }}>
                  Description
                </label>
                <Input
                  type="textarea"
                  style={{ borderRadius: 5, borderWidth: 1, gridColumn: "1/-1" }}
                  className={`form-control`}
                  value={form?.description}
                  onChange={e => handleFieldChange("description", e.target.value)}
                  placeholder="Description"
                />
                {/* {errors.description && (
                  <small className="text-danger" style={{ gridColumn: "1/-1" }}>
                    {errors.description}
                  </small>
                )} */}
              </>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Proceed Button */}
      <div className="d-flex justify-content-end">
        <StyledButton color="primary" onClick={handleGoBack}>
          <BiArrowBack style={{ marginRight: '4px' }} />Go Back
        </StyledButton>
        <StyledButton
          color="primary"
          // style={{ backgroundColor: "#8353E2", border: "none" }}
          onClick={proceed}
        >
          Next{" "}
          <i className="ph-caret-right" style={{ verticalAlign: "middle" }} />
        </StyledButton>
      </div>
    </>
  );
};

export default StepOne;
